import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { ApiError } from '@/types'
import { refreshAccessToken } from '@/lib/auth-token'
import { captureServerMessage, humanizeFieldError, isGenericMessage } from '@/lib/api-messages'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Track if we're currently refreshing to avoid multiple refresh calls
let isRefreshing = false
let failedQueue: Array<{
  resolve: (token: string) => void
  reject: (error: unknown) => void
}> = []

function processQueue(error: unknown, token: string | null = null) {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error)
    } else {
      p.resolve(token!)
    }
  })
  failedQueue = []
}

// Request interceptor: attach JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('access_token')
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// Endpoints where a 401 means "wrong credentials/token", not "session expired".
// Refreshing + redirecting there would reload the login page and swallow the
// error the form needs to show.
const NO_REFRESH_PATHS = ['/api/login', '/api/token/refresh', '/api/signup', '/api/password-reset']

function isNoRefreshRequest(url: string | undefined): boolean {
  if (!url) return false
  const path = url.replace(/^https?:\/\/[^/]+/, '')
  return NO_REFRESH_PATHS.some((p) => path.startsWith(p))
}

// Response interceptor: handle 401 + token refresh
apiClient.interceptors.response.use(
  (response) => {
    // Remember the server's success sentence ("Customer created successfully")
    // for mutation toasts — see serverMessage() in lib/api-messages.
    captureServerMessage(response)
    return response
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    // If 401 and we haven't already retried
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isNoRefreshRequest(originalRequest.url)
    ) {
      if (isRefreshing) {
        // Queue this request until refresh completes
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: (token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${token}`
              }
              resolve(apiClient(originalRequest))
            },
            reject,
          })
        })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Shared refresh path (deduped) — also used by the WebSocket clients
        // so REST and WS never diverge on token state.
        const newAccessToken = await refreshAccessToken()

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
        }

        processQueue(null, newAccessToken)
        return apiClient(originalRequest)
      } catch (refreshError) {
        processQueue(refreshError, null)
        // Clear auth state and redirect to login
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        localStorage.removeItem('auth-store')
        localStorage.removeItem('waba-store')
        window.location.href = '/login'
        return Promise.reject(refreshError)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(normalizeError(error))
  },
)

// Normalize various DRF error formats into a consistent shape
/** Parse a DRF field-error object ({field: ["msg", …] | "msg"}) into a
 *  normalized {field: ["msg", …]} map. Returns undefined when there's nothing
 *  usable so callers can treat "no field errors" as falsy. */
function parseFieldErrors(raw: unknown): Record<string, string[]> | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined
  const errors: Record<string, string[]> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(value)) errors[key] = value.map(String)
    else if (typeof value === 'string') errors[key] = [value]
  }
  return Object.keys(errors).length ? errors : undefined
}

function firstFieldError(errors: Record<string, string[]> | undefined): string | undefined {
  if (!errors) return undefined
  for (const [field, messages] of Object.entries(errors)) {
    if (messages[0]) return humanizeFieldError(field, messages[0])
  }
  return undefined
}

export function normalizeError(error: AxiosError): ApiError {
  const status = error.response?.status || 0
  const data = error.response?.data as Record<string, unknown> | undefined

  if (!data) {
    return {
      message: error.message || 'Network error. Please check your connection.',
      status,
    }
  }

  // DRF detail string
  if (typeof data.detail === 'string') {
    return { message: data.detail, status }
  }

  // Custom message field (template submit and other structured API errors).
  // Many endpoints (e.g. signup) return BOTH a generic top-level message
  // ("Signup failed.") AND a field-level ``errors`` object with the real
  // reason ("A company with this name already exists.") — carry the latter
  // through so callers can surface it instead of the generic message.
  if (typeof data.message === 'string') {
    const fieldErrors = parseFieldErrors(data.errors)
    return {
      // A placeholder ("Validation failed") says nothing — show the first
      // field error as a sentence instead.
      message: isGenericMessage(data.message)
        ? firstFieldError(fieldErrors) || data.message || 'Something went wrong. Please try again.'
        : data.message,
      title: typeof data.title === 'string' ? data.title : undefined,
      hints: Array.isArray(data.hints)
        ? data.hints.filter((item): item is string => typeof item === 'string')
        : undefined,
      validation_errors:
        typeof data.validation_errors === 'string' ? data.validation_errors : undefined,
      errors: fieldErrors,
      status,
      // Structured payload some errors carry (e.g. an inbox send's persisted
      // failed message row) — only when present.
      ...(data.data !== undefined ? { data: data.data } : {}),
    }
  }

  // Billing endpoints: { success: false, error: { code, message } }
  const billingError = data.error
  if (
    billingError &&
    typeof billingError === 'object' &&
    typeof (billingError as { message?: unknown }).message === 'string'
  ) {
    return {
      message: (billingError as { message: string }).message,
      status,
    }
  }

  // `{ error: "…" }` bodies built by hand in a few views.
  if (typeof billingError === 'string' && billingError.trim()) {
    return { message: billingError, status }
  }

  // Validation errors (field-level)
  if (typeof data === 'object' && !data.message && !data.detail) {
    const errors: Record<string, string[]> = {}
    let firstMessage = 'Validation error'

    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value)) {
        errors[key] = value.map(String)
        if (firstMessage === 'Validation error') {
          firstMessage = humanizeFieldError(key, String(value[0]))
        }
      } else if (typeof value === 'string') {
        errors[key] = [value]
        if (firstMessage === 'Validation error') {
          firstMessage = humanizeFieldError(key, value)
        }
      }
    }

    return { message: firstMessage, errors, status }
  }

  return {
    message: 'Something went wrong. Please try again.',
    status,
  }
}

export default apiClient
